"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Sparkles, CalendarDays, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import SectionContainer from "@/components/common/SectionContainer";
const elopementRates = [{
  tier: "Up to 25 Guests",
  duration: "2-Hour Lakeside Gazebo Rental",
  daytimeRate: "$1,750.00",
  sunsetRate: "$2,000.00",
  amenities: ["Gazebo site", "White garden chairs", "Bridal Suite access", "Venue restrooms"]
}, {
  tier: "Up to 50 Guests",
  duration: "4-Hour Lakeside Gazebo Rental",
  daytimeRate: "$2,000.00",
  sunsetRate: "$2,500.00",
  amenities: ["Gazebo site", "White garden chairs", "Bridal Suite access", "Venue restrooms"]
}];
const microRates = [{
  day: "Sunday - Thursday",
  schedule: "Sunday – Thursday",
  price: "$4,800 - $5,100",
  popular: false,
  description: "Perfect for gatherings Sunday through Thursday. Ideal for up to 50–100 guests with flexible setup and timing options."
}, {
  day: "Friday",
  schedule: "Friday Evening",
  price: "$5,100 - $5,400",
  popular: false,
  description: "Friday evenings offer great rates for your special event. Accommodates up to 50–100 guests with full service."
}, {
  day: "Saturday",
  schedule: "Prime Weekend",
  price: "$5,400 - $5,700",
  popular: true,
  description: "Premium Saturday availability for your perfect weekend celebration. Supports up to 50–100 guests with premium amenities."
}];
const fullRates = [{
  day: "Sunday - Thursday",
  schedule: "Sunday – Thursday",
  price: "$5,400 - $6,300",
  popular: false,
  description: "Full venue access Sunday through Thursday for up to 100–200 guests. Perfect for mid-week celebrations with exclusive access to all ceremony and reception spaces."
}, {
  day: "Friday",
  schedule: "Friday Evening",
  price: "$5,700 - $6,600",
  popular: false,
  description: "Exclusive multi-area celebration space on Friday evenings for up to 100–200 guests. Ideal for sophisticated gatherings with dedicated access to our premium event areas."
}, {
  day: "Saturday",
  schedule: "Prime Weekend",
  price: "$6,000 - $6,900",
  popular: true,
  description: "Full-scale ceremony and banquet access with dedicated venue prep on Saturday for up to 100–200 guests. Our most comprehensive package for your special day."
}];
export default function TabbedPricing() {
  const [activeTab, setActiveTab] = useState<"elopement" | "micro" | "full">("micro");
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("pricing-tab-change", {
      detail: {
        tab: activeTab
      }
    }));
  }, [activeTab]);
  const shouldReduceMotion = useReducedMotion();
  const currentRates = activeTab === "micro" ? microRates : fullRates;
  return <section id="tabbedpricingcards" data-nav="light" className="w-full bg-background text-foreground py-8 md:py-12">
      {/* Off-Peak Banner */}
      <div className="w-full border-y border-border bg-secondary/70 py-4 mb-12">
        <SectionContainer>
          <div className="flex items-center justify-center gap-2.5 text-center">
            <Sparkles className="size-4 text-foreground/80 shrink-0" />
            <p className="text-xs sm:text-sm font-semibold tracking-widest uppercase text-secondary-foreground font-default">
              <span className="font-bold">Save Up to 20% on Select Dates</span> | Off-Peak Season Rates Available (June–August &amp; December–February)
            </p>
          </div>
        </SectionContainer>
      </div>

      <SectionContainer>
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-default font-normal tracking-tight text-foreground mb-4">
            Rental rates by event size
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Select your estimated guest count below to view standard daily rates across weekdays and weekends.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex flex-col sm:flex-row p-1.5 rounded-xl bg-muted border border-border gap-2 w-full sm:w-auto">
            <Button type="button" variant={activeTab === "elopement" ? "default" : "ghost"} onClick={() => setActiveTab("elopement")} className={`rounded-lg px-6 py-3 text-sm font-medium transition-colors cursor-pointer w-full sm:w-auto ${activeTab === "elopement" ? "bg-primary text-primary-foreground shadow-sm" : "text-foreground hover:bg-background/60"}`}>
              Lakeside Elopement
            </Button>
            <Button type="button" variant={activeTab === "micro" ? "default" : "ghost"} onClick={() => setActiveTab("micro")} className={`rounded-lg px-6 py-3 text-sm font-medium transition-colors cursor-pointer w-full sm:w-auto ${activeTab === "micro" ? "bg-primary text-primary-foreground shadow-sm" : "text-foreground hover:bg-background/60"}`}>
              Micro Events (Up to 100)
            </Button>
            <Button type="button" variant={activeTab === "full" ? "default" : "ghost"} onClick={() => setActiveTab("full")} className={`rounded-lg px-6 py-3 text-sm font-medium transition-colors cursor-pointer w-full sm:w-auto ${activeTab === "full" ? "bg-primary text-primary-foreground shadow-sm" : "text-foreground hover:bg-background/60"}`}>
              Full Events (Up to 200)
            </Button>
          </div>
        </div>

        {/* Elopement Lease Policy Note */}
        {activeTab === "elopement" && <div className="text-center mb-8">
            <p className="text-xs sm:text-sm uppercase tracking-wider text-muted-foreground font-default font-medium">
              2-4 Hour Gazebo Lease | Mon–Thu (90 days prior) • Fri–Sun (30 days prior)
            </p>
          </div>}

        {/* Micro Events Duration Note */}
        {activeTab === "micro" && <div className="text-center mb-8">
            <p className="text-xs sm:text-sm uppercase tracking-wider text-muted-foreground font-default font-medium">
              Up to 7-Hour Venue Rental
            </p>
          </div>}

        {/* Full Events Duration Note */}
        {activeTab === "full" && <div className="text-center mb-8">
            <p className="text-xs sm:text-sm uppercase tracking-wider text-muted-foreground font-default font-medium">
              Up to 12-Hour Venue Rental
            </p>
          </div>}

        {/* Rate Cards Grid */}
        <AnimatePresence mode="wait">
          {activeTab === "elopement" ? <motion.div key="elopement" initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.35,
          ease: "easeOut"
        }} className="grid grid-cols-1 sm:grid-cols-2 max-w-4xl mx-auto gap-6 items-stretch">
              {elopementRates.map((rate, i) => <div data-index={i} key={`elopement-${rate.tier}`} className="h-full flex flex-col">
                  <Card className="h-full flex flex-col justify-between rounded-xl bg-card transition-shadow duration-200 border border-border shadow-sm hover:shadow-md">
                    <div>
                      <CardHeader className="pt-6 pb-4">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <CardTitle className="text-xl font-default font-semibold text-card-foreground">
                            {rate.tier}
                          </CardTitle>
                          <CalendarDays className="size-4 text-muted-foreground shrink-0" />
                        </div>
                        <p className="text-xs text-muted-foreground font-default uppercase tracking-wider">
                          {rate.duration}
                        </p>
                      </CardHeader>

                      <CardContent className="space-y-4">
                        <div className="border-b border-border pb-4 space-y-3">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="text-xs uppercase tracking-wider text-muted-foreground font-default">
                              Daytime Rate
                            </span>
                            <span className="text-2xl sm:text-3xl font-default font-bold text-foreground">
                              {rate.daytimeRate}
                            </span>
                          </div>
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="text-xs uppercase tracking-wider text-muted-foreground font-default">
                              Sunset Rate
                            </span>
                            <span className="text-2xl sm:text-3xl font-default font-bold text-foreground">
                              {rate.sunsetRate}
                            </span>
                          </div>
                        </div>

                        <ul className="space-y-2 pt-2 text-xs text-muted-foreground font-default">
                          {rate.amenities.map((amenity, aIdx) => <li key={aIdx} className="flex items-center gap-2">
                              <Check className="size-3.5 text-foreground shrink-0" />
                              <span>{amenity}</span>
                            </li>)}
                        </ul>
                      </CardContent>
                    </div>

                    <CardFooter className="pt-4 border-t border-border mt-auto">
                      <Button asChild variant="outline" className="w-full rounded-md font-default">
                        <Link to="#contacthero">
                          Inquire for {rate.tier}
                        </Link>
                      </Button>
                    </CardFooter>
                  </Card>
                </div>)}
            </motion.div> : <motion.div key={activeTab} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.35,
          ease: "easeOut"
        }} className={`grid grid-cols-1 sm:grid-cols-2 ${currentRates.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4"} gap-6 items-stretch`}>
              {currentRates.map((rate, i) => <div data-index={i} key={`${activeTab}-${rate.day}`} className="h-full flex flex-col">
                  <Card className={`h-full flex flex-col justify-between rounded-xl bg-card transition-shadow duration-200 relative ${rate.popular ? "border-2 border-primary shadow-md ring-1 ring-primary/20" : "border border-border shadow-sm hover:shadow-md"}`}>
                    <div>
                      {rate.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <Badge className="bg-primary text-primary-foreground rounded-full px-3 py-0.5 text-xs font-semibold tracking-wide shadow-xs">
                            Most Popular
                          </Badge>
                        </div>}

                      <CardHeader className={`pb-4 ${rate.popular ? "pt-8" : "pt-6"}`}>
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <CardTitle className="text-xl font-default font-semibold text-card-foreground">
                            {rate.day}
                          </CardTitle>
                          <CalendarDays className="size-4 text-muted-foreground shrink-0" />
                        </div>
                        <p className="text-xs text-muted-foreground font-default uppercase tracking-wider">
                          {rate.schedule}
                        </p>
                      </CardHeader>

                      <CardContent className="space-y-4">
                        <div className="border-b border-border pb-4">
                          <div className="text-3xl sm:text-4xl font-default font-bold text-foreground">
                            {rate.price}
                          </div>
                          <span className="text-xs text-muted-foreground font-default">
                            Venue rental fee
                          </span>
                        </div>

                        <p className="text-sm text-muted-foreground font-default leading-relaxed min-h-[4rem]">
                          {rate.description}
                        </p>

                        <ul className="space-y-2 pt-2 text-xs text-muted-foreground font-default">
                          <li className="flex items-center gap-2">
                            <Check className="size-3.5 text-foreground shrink-0" />
                            <span>Indoor banquet hall &amp; outdoor pavilion</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <Check className="size-3.5 text-foreground shrink-0" />
                            <span>Tables, chairs &amp; basic setup included</span>
                          </li>
                        </ul>
                      </CardContent>
                    </div>

                    <CardFooter className="pt-4 border-t border-border mt-auto">
                      <Button asChild variant={rate.popular ? "default" : "outline"} className="w-full rounded-md font-default">
                        <Link to="#contacthero">
                          Inquire for {rate.day}
                        </Link>
                      </Button>
                    </CardFooter>
                  </Card>
                </div>)}
            </motion.div>}
        </AnimatePresence>

        {/* Subtle Policy Note */}
        <div className="mt-12 text-center text-xs text-muted-foreground max-w-2xl mx-auto font-default">
          <p>
            * Holiday weekends and peak dates are subject to custom holiday pricing. Security deposit and cleaning fee apply to all private rentals.
          </p>
        </div>
      </SectionContainer>
    </section>;
}